import { useEffect, useRef, useState } from "react"

import { App, Button, Flex, Modal } from "@/atoms"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { CheckOutlined, EditOutlined, SafetyCertificateOutlined } from "@/icons"
import { useAiPolicies } from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import { SuggestionRow } from "./SuggestionRow"

import styles from "./HardenAgent.module.scss"

import type { HardeningSuggestion } from "@/pages/GovernancePage/applications/policies/getHardeningSuggestions.types"
import type { HardenModalProps } from "./HardenAgent.types"

const MODAL_WIDTH = 1150
// Simulates applying the rules before the policy is saved.
const APPROVE_DELAY_MS = 2000

export const HardenModal = (props: HardenModalProps) => {
  const { open, agent, suggestions, onClose, onApproved } = props
  const { t } = useTranslation("translation", {
    keyPrefix: "governance.ai.harden.modal"
  })
  const { t: tForms } = useTranslation("translation", {
    keyPrefix: "forms.messages"
  })
  const { message } = App.useApp()
  const { logger, EVENTS } = useLogger()
  const { approveSuggestions } = useAiPolicies()
  const [picked, setPicked] = useState<string[]>([])
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState<Record<string, string[]>>({})
  const [approving, setApproving] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  // Every opening starts from the suggestions' own defaults.
  useEffect(() => {
    if (!open) return

    setPicked(suggestions.filter((s) => s.checked).map((s) => s.id))
    setValues({})
    setEditing(false)
  }, [open, suggestions])

  // The suggestion as shown, with any values edited in the modal.
  const withValues = (suggestion: HardeningSuggestion): HardeningSuggestion => {
    const value = values[suggestion.id] ?? suggestion.condition.value

    return { ...suggestion, condition: { ...suggestion.condition, value } }
  }
  const shown = suggestions.map(withValues)
  // A tool can be both a source and a sink; list it once.
  const tools = [
    ...new Map(
      [...agent.dataSources, ...agent.dataSinks]
        .flatMap((group) => group.tools ?? [])
        .map((tool) => [
          tool.name,
          { name: tool.name, dataCategory: tool.dataCategory }
        ])
    ).values()
  ]
  const pickedSuggestions = shown.filter((s) => picked.includes(s.id))
  const count = pickedSuggestions.length
  const hasEmptyValue = pickedSuggestions.some(
    (s) => s.condition.value.length === 0
  )
  const toggle = (id: string) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )

  const save = () => {
    const record = approveSuggestions(agent, pickedSuggestions)

    message.success(tForms("created", { name: record.policyName }))

    logger.info(EVENTS.ANALYTIC_EVENTS.AI_POLICY.HARDEN.APPROVED, {
      agent_id: agent.id,
      approved_ids: picked,
      unticked_ids: suggestions
        .filter((s) => !picked.includes(s.id))
        .map((s) => s.id)
    })
    setApproving(false)
    onApproved()
  }

  const approve = () => {
    setApproving(true)
    timer.current = setTimeout(save, APPROVE_DELAY_MS)
  }

  const toggleEditing = () => {
    if (!editing)
      logger.info(EVENTS.ANALYTIC_EVENTS.AI_POLICY.HARDEN.EDIT_OPENED, {
        agent_id: agent.id,
        ticked_ids: picked
      })
    setEditing((prev) => !prev)
  }

  return (
    <Modal
      centered
      destroyOnHidden
      open={open}
      width={MODAL_WIDTH}
      onCancel={onClose}
      closable={!approving}
      maskClosable={!approving}
      keyboard={!approving}
      className={styles.modal}
      classNames={{
        header: styles.modalHeader,
        body: styles.modalBody,
        footer: styles.modalFooter
      }}
      title={
        <Flex align="center" gap={12}>
          <span className={styles.headerIcon}>
            <SafetyCertificateOutlined />
          </span>
          <Flex vertical>
            <span className={styles.headerTitle}>
              {t("title", { agent: agent.name })}
            </span>
            <span className={styles.headerSubtitle}>{t("subtitle")}</span>
          </Flex>
        </Flex>
      }
      footer={
        <Flex align="center" justify="space-between" gap={8}>
          <Button
            icon={editing ? <CheckOutlined /> : <EditOutlined />}
            disabled={approving}
            onClick={toggleEditing}
          >
            {t(editing ? "doneEditing" : "edit")}
          </Button>
          <Flex align="center" gap={8}>
            <Button disabled={approving} onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button
              type="primary"
              loading={approving}
              disabled={count === 0 || hasEmptyValue || editing}
              onClick={approve}
            >
              {t("apply", { count })}
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Flex vertical gap={10}>
        {shown.map((suggestion) => (
          <SuggestionRow
            key={suggestion.id}
            suggestion={suggestion}
            checked={picked.includes(suggestion.id)}
            editing={editing}
            tools={tools}
            onToggle={() => toggle(suggestion.id)}
            onChangeValue={(value) =>
              setValues((prev) => ({ ...prev, [suggestion.id]: value }))
            }
          />
        ))}
      </Flex>
    </Modal>
  )
}
